import React, { useState, useRef } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { ComboboxField } from "../formElements/ComboboxField";

const EMPTY_FILTER = {
  column: "",
  value: "",
  value2: "",
  dropDown: false,
  options: [],
  range: false,
  numberRange: false,
  month: false,
  date: false,
  monthValue: "",
  yearValue: "",
  monthOrDate: "month",
};

const MultiColumnFilter = ({
  columns,
  data,
  onFilter,
  extra = [],
  output = "array",
  onCancel,
}) => {
  const [filters, setFilters] = useState([{ ...EMPTY_FILTER }]);

  const getKey = (col) => col.selectorKey || col.id;

  // ✅ Store stable refs for handlers — avoids new function on every render
  const columnHandlersRef = useRef({});
  const valueHandlersRef = useRef({});

  // ✅ Get or create a stable column change handler for a given index
  const getColumnHandler = (index) => {
    if (!columnHandlersRef.current[index]) {
      columnHandlersRef.current[index] = (selected) => {
        if (!selected?.code) return;
        const selectedColumn = selected.code;
        const colObj = columns.find((col) => getKey(col) === selectedColumn);

        let options = [];
        if (colObj?.dropDown) {
          if (colObj?.staticOptions?.length > 0) {
            options = colObj.staticOptions;
          } else if (data && Array.isArray(data)) {
            if (colObj?.nestedArray && colObj?.nestedPath) {
              options = [
                ...new Set(
                  data
                    .flatMap((row) => row[colObj.nestedPath] || [])
                    .map((item) => item[colObj.selectorKey])
                    .filter(Boolean),
                ),
              ];
            } else {
              options = [
                ...new Set(
                  data.map((row) => row[selectedColumn]).filter(Boolean),
                ),
              ];
            }
          }
        }

        setFilters((prev) => {
          const newFilters = [...prev];
          newFilters[index] = {
            column: selectedColumn,
            value: "",
            value2: "",
            dropDown: colObj?.dropDown || false,
            options,
            range: colObj?.range || false,
            numberRange: colObj?.numberRange || false,
            month: colObj?.month === true,
            date: colObj?.date === true,
            combined: colObj?.combined || false,
            combinedFields: colObj?.combinedFields || [],
            nestedArray: colObj?.nestedArray || false,
            nestedPath: colObj?.nestedPath || "",
            selectorKey: colObj?.selectorKey || selectedColumn,
            monthValue: "",
            yearValue: "",
            monthOrDate: "month",
          };
          return newFilters;
        });
      };
    }
    return columnHandlersRef.current[index];
  };

  // ✅ Get or create a stable value change handler for a given index
  const getValueHandler = (index) => {
    if (!valueHandlersRef.current[index]) {
      valueHandlersRef.current[index] = (selected) => {
        if (!selected?.name) return;
        setFilters((prev) => {
          const newFilters = [...prev];
          newFilters[index] = { ...newFilters[index], value: selected.name };
          return newFilters;
        });
      };
    }
    return valueHandlersRef.current[index];
  };

  const handleValueChange = (index, value, second = false) => {
    const newFilters = [...filters];
    if (second) newFilters[index].value2 = value;
    else newFilters[index].value = value;
    setFilters(newFilters);
  };

  const handleMonthYearChange = (index, type, value) => {
    const newFilters = [...filters];
    if (type === "month") newFilters[index].monthValue = value;
    if (type === "year") newFilters[index].yearValue = value;
    setFilters(newFilters);
  };

  const handleMonthOrDateChange = (index, value) => {
    const newFilters = [...filters];
    newFilters[index].monthOrDate = value;
    setFilters(newFilters);
  };

  const addFilterRow = () => {
    setFilters([...filters, { ...EMPTY_FILTER }]);
  };

  const removeFilterRow = (index) => {
    // ✅ Clean up handlers for removed index
    delete columnHandlersRef.current[index];
    delete valueHandlersRef.current[index];
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters.length ? newFilters : [{ ...EMPTY_FILTER }]);
  };

  const buildAppliedFiltersArray = () =>
    filters
      .filter(
        (f) =>
          f.column &&
          (f.value ||
            f.value2 ||
            (f.month &&
              f.monthOrDate === "month" &&
              f.monthValue &&
              f.yearValue)),
      )
      .map((f) => {
        if (f.month && f.monthOrDate === "month") {
          const year = f.yearValue;
          const month = String(f.monthValue).padStart(2, "0");
          const start = `${year}-${month}-01T00:00:00.000Z`;
          const lastDay = new Date(year, month, 0).getDate();
          const end = `${year}-${month}-${lastDay}T23:59:59.999Z`;
          return { [f.column]: { $gte: start, $lte: end } };
        }
        if ((f.month && f.monthOrDate === "date") || f.date) {
          if (!f.value || !f.value2) return null;
          const start = `${f.value}T00:00:00.000Z`;
          const end = `${f.value2}T23:59:59.999Z`;
          return { [f.column]: { $gte: start, $lte: end } };
        }
        if (f.numberRange) {
          const from = parseFloat(f.value);
          const to = parseFloat(f.value2);
          if (isNaN(from) || isNaN(to)) return null;
          if (from > to) return null;
          return { [f.column]: { $gte: from, $lte: to } };
        }
        if (f.range) {
          return { [f.column]: { $gte: f.value, $lte: f.value2 } };
        }
        if (f.combined && f.combinedFields?.length > 0) {
          return {
            $or: f.combinedFields.map((field) => ({
              [field]: { $regex: `.*${f.value}.*`, $options: "i" },
            })),
          };
        }
        if (f.nestedArray && f.nestedPath) {
          const fieldKey = f.selectorKey || f.column;
          return {
            [f.nestedPath]: {
              $elemMatch: {
                [fieldKey]: { $regex: `.*${f.value}.*`, $options: "i" },
              },
            },
          };
        }
        return {
          [f.column]: { $regex: `.*${f.value}.*`, $options: "i" },
        };
      })
      .filter(Boolean);

  const handleApplyFilters = () => {
    const appliedFilters = buildAppliedFiltersArray();
    let finalResult;
    if (output === "array") {
      finalResult = appliedFilters;
    } else if (output === "and") {
      finalResult = appliedFilters.length > 0 ? { $and: appliedFilters } : {};
    } else {
      const hasRangeOrMonth = filters.some(
        (f) =>
          f.range || f.numberRange || (f.month && f.monthOrDate === "month"),
      );
      finalResult = hasRangeOrMonth
        ? appliedFilters.length > 0
          ? { $and: appliedFilters }
          : {}
        : appliedFilters;
    }
    onFilter(finalResult);
  };

  const handleClearFilters = () => {
    // ✅ Clear all cached handlers on clear
    columnHandlersRef.current = {};
    valueHandlersRef.current = {};
    setFilters([{ ...EMPTY_FILTER }]);
    if (output === "array") onFilter([]);
    else onFilter({});
  };

  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  const years = Array.from(
    { length: 20 },
    (_, i) => new Date().getFullYear() - i,
  );

  return (
    <div
      style={{
        border: "1px dashed #ccc",
        padding: "15px",
        borderRadius: "6px",
        marginTop: "10px",
        marginBottom: "10px",
      }}
    >
      {filters.map((filter, index) => (
        <Row key={index} className="align-items-center mb-2">
          {/* ✅ Column Selector — stable handler via ref */}
          <Col xs={4} className="pe-3">
            <ComboboxField
              id={`filter-column-${index}`}
              name={`column-${index}`}
              label="Action"
              placeholder="Select Action"
              data={columns.map((col) => ({
                id: getKey(col),
                name: col.name,
                code: getKey(col),
                description: col.name,
              }))}
              setValue={
                filter.column
                  ? {
                      id: filter.column,
                      name:
                        columns.find((col) => getKey(col) === filter.column)
                          ?.name || "",
                    }
                  : ""
              }
              getvalue={getColumnHandler(index)} // ✅ stable ref-based handler
              disabled={false}
            />
          </Col>

          {/* Value Input Area */}
          <Col xs={4} className="px-3">
            {filter.column ? (
              <>
                {filter.month || filter.date ? (
                  <div>
                    {filter.month && (
                      <Form.Select
                        size="sm"
                        value={filter.monthOrDate}
                        onChange={(e) =>
                          handleMonthOrDateChange(index, e.target.value)
                        }
                        className="mb-2"
                      >
                        <option value="month">Month-wise</option>
                        <option value="date">Date-wise</option>
                      </Form.Select>
                    )}
                    {filter.month && filter.monthOrDate === "month" ? (
                      <Row className="g-3 align-items-center">
                        <Col className="commTopButtonRightLeftPadding">
                          <Form.Label className="mb-2 small">Month</Form.Label>
                          <Form.Select
                            size="sm"
                            value={filter.monthValue}
                            onChange={(e) =>
                              handleMonthYearChange(
                                index,
                                "month",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Month</option>
                            {months.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col className="commTopButtonRightLeftPadding">
                          <Form.Label className="mb-2 small">Year</Form.Label>
                          <Form.Select
                            size="sm"
                            value={filter.yearValue}
                            onChange={(e) =>
                              handleMonthYearChange(
                                index,
                                "year",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Year</option>
                            {years.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    ) : (
                      <Row className="g-3">
                        <Col className="commTopButtonRightLeftPadding">
                          <Form.Label className="mb-2 small">
                            From Date
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={filter.value}
                            onChange={(e) =>
                              handleValueChange(index, e.target.value)
                            }
                          />
                        </Col>
                        <Col className="commTopButtonRightLeftPadding">
                          <Form.Label className="mb-2 small">
                            To Date
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={filter.value2}
                            onChange={(e) =>
                              handleValueChange(index, e.target.value, true)
                            }
                          />
                        </Col>
                      </Row>
                    )}
                  </div>
                ) : filter.numberRange ? (
                  <Row className="g-3">
                    <Col xs={6} className="commTopButtonRightLeftPadding">
                      <Form.Label className="mb-2 small">From</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="From"
                        value={filter.value}
                        style={{ height: "40px", fontSize: "18px" }}
                        isInvalid={
                          filter.value !== "" && isNaN(parseFloat(filter.value))
                        }
                        onChange={(e) =>
                          handleValueChange(index, e.target.value)
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        Enter a valid number
                      </Form.Control.Feedback>
                    </Col>
                    <Col xs={6} className="commTopButtonRightLeftPadding">
                      <Form.Label className="mb-2 small">To</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="To"
                        value={filter.value2}
                        style={{ height: "40px", fontSize: "18px" }}
                        isInvalid={
                          filter.value2 !== "" &&
                          (isNaN(parseFloat(filter.value2)) ||
                            parseFloat(filter.value2) <
                              parseFloat(filter.value))
                        }
                        onChange={(e) =>
                          handleValueChange(index, e.target.value, true)
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        Must be ≥ From value
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                ) : filter.range ? (
                  <Row className="g-3">
                    <Col xs={6} className="commTopButtonRightLeftPadding">
                      <Form.Label className="mb-2 small">From</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="From"
                        value={filter.value}
                        style={{ height: "40px", fontSize: "18px" }}
                        onChange={(e) =>
                          handleValueChange(index, e.target.value)
                        }
                      />
                    </Col>
                    <Col xs={6} className="commTopButtonRightLeftPadding">
                      <Form.Label className="mb-2 small">To</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="To"
                        value={filter.value2}
                        style={{ height: "40px", fontSize: "18px" }}
                        onChange={(e) =>
                          handleValueChange(index, e.target.value, true)
                        }
                      />
                    </Col>
                  </Row>
                ) : filter.dropDown ? (
                  // ✅ Value ComboboxField — stable handler via ref
                  <ComboboxField
                    id={`filter-value-${index}`}
                    name={`value-${index}`}
                    label="Value"
                    placeholder="Select Value"
                    data={filter.options.map((val, i) => ({
                      id: i,
                      name: val,
                      code: val,
                      description: val,
                    }))}
                    setValue={
                      filter.value
                        ? {
                            id: filter.options.indexOf(filter.value),
                            name: filter.value,
                          }
                        : ""
                    }
                    getvalue={getValueHandler(index)} // ✅ stable ref-based handler
                    disabled={false}
                  />
                ) : (
                  <>
                    <Form.Label className="mb-2 small">Value</Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Enter value"
                      style={{ height: "40px", fontSize: "18px" }}
                      value={filter.value}
                      onChange={(e) => handleValueChange(index, e.target.value)}
                    />
                  </>
                )}
              </>
            ) : (
              <div style={{ height: "31px" }} />
            )}
          </Col>

          {/* Add / Remove Buttons */}
          <Col xs={4} className="ps-4 pt-4">
            <div className="d-flex gap-2">
              {filter.column && (
                <Button size="md" variant="secondary" onClick={addFilterRow}>
                  +
                </Button>
              )}
              {filters.length > 1 && (
                <Button
                  size="md"
                  variant="danger"
                  onClick={() => removeFilterRow(index)}
                >
                  -
                </Button>
              )}
            </div>
          </Col>
        </Row>
      ))}

      {/* Action Buttons */}
      <Row className="mt-3">
        <Col xs="auto">
          <Button
            variant="success"
            onClick={handleApplyFilters}
            style={{ minWidth: "70px" }}
            className="me-2"
          >
            Search
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            variant="danger"
            onClick={handleClearFilters}
            style={{ minWidth: "70px" }}
            className="me-2"
          >
            Clear
          </Button>
        </Col>
        <Col xs="auto">
          <Button
            variant="danger"
            onClick={() => {
              columnHandlersRef.current = {};
              valueHandlersRef.current = {};
              setFilters([{ ...EMPTY_FILTER }]);
              onCancel();
            }}
            style={{ minWidth: "70px" }}
          >
            Cancel
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default MultiColumnFilter;
